/**
 * As peças do mapa que a leitura e a edição usam: o bloco, a ligação, a
 * legenda das cores e as ferramentas (zoom e "Organizar").
 *
 * O bloco tem o ícone do tipo na cor do grupo, o contador do que falta e cada
 * saída com o ponto na cor dela; na edição, a saída livre ganha o "+" do
 * próximo passo. A ligação pinta a cor da saída de onde sai e, escolhida na
 * edição, ganha o "×" no meio para tirá-la. Tudo componente nosso: a folha da
 * biblioteca só dá a estrutura (ADR-0024).
 */

import { type CSSProperties, type ReactNode } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Panel,
  Position,
  getBezierPath,
  useReactFlow,
  useViewport,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import { Botao, Icone } from '@/shared/ui';
import { COR_DO_TOM, iconeDoTipo, tomDaSaida, type TomDaSaida } from '../lib/aparencia';
import type { BlocoDesenhado, LigacaoDesenhada } from '../lib/desenho';

export type DadosDoBloco = Omit<BlocoDesenhado, 'id' | 'posicao'> & {
  /** Quantas coisas faltam acertar neste bloco (0: nada). */
  faltam: number;
  novo: boolean;
  /** Na simulação mostrada no mapa: o bloco de agora, um já percorrido, um fora do caminho. */
  passo?: 'agora' | 'feito' | 'fora' | 'falhou';
  /** Na edição: o "+" de uma saída livre, que abre a escolha do próximo passo. */
  aoProximo?: (saida: string, ordem: number) => void;
} & Record<string, unknown>;

export type DadosDaLigacao = {
  rotulo: string | null;
  tom: TomDaSaida;
  /** Na edição: o "×" da ligação escolhida. */
  aoTirar?: () => void;
  /** Na simulação mostrada no mapa: fora do caminho, já percorrida, ou a que corre agora. */
  caminho?: 'fora' | 'feita' | 'correndo';
} & Record<string, unknown>;

export function BlocoDoMapa({
  data,
  selected,
  dragging,
  isConnectable,
}: NodeProps<Node<DadosDoBloco>>): ReactNode {
  return (
    <div
      className="editor-do-mapa__bloco"
      data-grupo={data.grupo}
      data-pendente={data.faltam > 0 ? 'sim' : undefined}
      data-selecionado={selected ? 'sim' : undefined}
      data-arrastando={dragging ? 'sim' : undefined}
      data-novo={data.novo ? 'sim' : undefined}
      data-passo={data.passo}
    >
      {data.grupo !== 'comeco' && (
        <Handle
          type="target"
          position={Position.Left}
          className="editor-do-mapa__entrada"
          isConnectable={isConnectable}
        />
      )}
      <div className="editor-do-mapa__bloco-topo">
        <span className="editor-do-mapa__bloco-icone" aria-hidden="true">
          <Icone nome={iconeDoTipo(data.tipo)} />
        </span>
        <div className="editor-do-mapa__bloco-textos">
          <p className="editor-do-mapa__bloco-titulo">{data.titulo}</p>
          <p className="editor-do-mapa__bloco-resumo">{data.resumo}</p>
        </div>
        {data.faltam > 0 && (
          <span className="editor-do-mapa__bloco-pendencia">
            <Icone nome="alerta" />
            {/* O espaço vai junto: sem ele o leitor de tela lê "Falta acertar2". */}
            <span className="visualmente-oculto">{'Falta acertar'} </span>
            {data.faltam}
          </span>
        )}
      </div>
      <ul className="editor-do-mapa__saidas">
        {data.saidas.map((s, i) => (
          <li
            key={s.id}
            className="editor-do-mapa__saida-linha"
            style={{ '--tom': COR_DO_TOM[tomDaSaida(s.id)] } as CSSProperties}
          >
            <span className="editor-do-mapa__saida-ponto" aria-hidden="true" />
            {s.rotulo}
            {data.aoProximo && s.livre && (
              <button
                type="button"
                className="editor-do-mapa__mais nodrag"
                aria-label={`Próximo passo depois de “${s.rotulo}”, em ${data.titulo}`}
                title="Criar o próximo passo"
                onClick={(e) => {
                  e.stopPropagation();
                  data.aoProximo!(s.id, i);
                }}
              >
                <Icone nome="criar" />
              </button>
            )}
            <Handle
              type="source"
              id={s.id}
              position={Position.Right}
              className="editor-do-mapa__saida"
              isConnectable={isConnectable}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LigacaoDoMapa({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  selected,
  data,
}: EdgeProps<Edge<DadosDaLigacao>>): ReactNode {
  const [caminho, x, y] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });
  const cor = COR_DO_TOM[data?.tom ?? 'padrao'];
  const tirar = selected && data?.aoTirar;
  /*
   * A cor vai pelas variáveis do React Flow, e não por `stroke` no traço: o
   * estilo direto no traço vencia a regra de escolhida e a de foco, e a ligação
   * escolhida ou focada pelo teclado não mudava (ADR-0024). A espessura e o
   * realce da simulação vêm das classes.
   */
  const classes = [
    'editor-do-mapa__ligacao',
    selected ? 'editor-do-mapa__ligacao--escolhida' : null,
    data?.caminho ? `editor-do-mapa__ligacao--${data.caminho}` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <>
      <BaseEdge
        path={caminho}
        {...(markerEnd ? { markerEnd } : {})}
        className={classes}
        style={
          {
            '--xy-edge-stroke': cor,
            '--xy-edge-stroke-selected': cor,
          } as CSSProperties
        }
        interactionWidth={18}
      />
      {(data?.rotulo || tirar) && (
        <EdgeLabelRenderer>
          <div
            className="editor-do-mapa__no-meio nodrag nopan"
            data-fora={data?.caminho === 'fora' ? 'sim' : undefined}
            style={
              {
                transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                '--tom': cor,
              } as CSSProperties
            }
          >
            {data?.rotulo && (
              <span className="editor-do-mapa__rotulo-da-ligacao">{data.rotulo}</span>
            )}
            {tirar && (
              <button
                type="button"
                className="editor-do-mapa__tirar"
                aria-label={data.rotulo ? `Tirar a ligação “${data.rotulo}”` : 'Tirar a ligação'}
                title="Tirar a ligação"
                onClick={(e) => {
                  e.stopPropagation();
                  data.aoTirar!();
                }}
              >
                <Icone nome="fechar" />
              </button>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const TIPOS_DE_BLOCO = { bloco: BlocoDoMapa };
export const TIPOS_DE_LIGACAO = { ligacao: LigacaoDoMapa };

/** A ligação para o React Flow, a partir do desenho. */
export function ligacaoParaOMapa(
  l: LigacaoDesenhada,
  extra: Partial<Edge<DadosDaLigacao>> & {
    aoTirar?: () => void;
    caminho?: DadosDaLigacao['caminho'];
  } = {},
): Edge<DadosDaLigacao> {
  const { aoTirar, caminho, ...resto } = extra;
  return {
    id: l.id,
    type: 'ligacao',
    source: l.de,
    sourceHandle: l.saida,
    target: l.para,
    markerEnd: { type: MarkerType.ArrowClosed, color: COR_DO_TOM[l.tom] },
    data: {
      rotulo: l.rotulo,
      tom: l.tom,
      ...(aoTirar ? { aoTirar } : {}),
      ...(caminho ? { caminho } : {}),
    },
    ...resto,
  };
}

export function LegendaDoMapa(): ReactNode {
  return (
    // A classe no painel limita a largura: a legenda não cobre o zoom no plano estreito.
    <Panel position="bottom-right" className="editor-do-mapa__canto-da-legenda">
      <ul className="editor-do-mapa__legenda" aria-label="O que cada cor de ligação quer dizer">
        {(
          [
            ['padrao', 'próximo'],
            ['sim', 'sim'],
            ['nao', 'não'],
            ['tempo', 'tempo esgotado'],
            ['caso', 'um caso'],
          ] as const
        ).map(([tom, rotulo]) => (
          <li key={tom} style={{ '--tom': COR_DO_TOM[tom] } as CSSProperties}>
            {rotulo}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * O zoom (afastar, a porcentagem, aproximar, caber na tela) e, na edição, o
 * "Organizar". A porcentagem volta para 100% ao clicar.
 */
export function FerramentasDoMapa({ aoOrganizar }: { aoOrganizar?: () => void }): ReactNode {
  const mapa = useReactFlow();
  const { zoom } = useViewport();
  const duracao = movimentoReduzido() ? 0 : 260;
  return (
    <Panel position="bottom-left">
      <div className="editor-do-mapa__ferramentas" role="toolbar" aria-label="Ver o mapa">
        <Botao
          variante="fantasma"
          miudo
          icone
          rotulo="Afastar"
          onClick={() => void mapa.zoomOut({ duration: duracao })}
        >
          <span aria-hidden="true">−</span>
        </Botao>
        <button
          type="button"
          className="editor-do-mapa__zoom"
          title="Voltar para 100%"
          aria-label={`Zoom em ${Math.round(zoom * 100)}%. Voltar para 100%`}
          onClick={() => void mapa.zoomTo(1, { duration: duracao })}
        >
          {Math.round(zoom * 100)}%
        </button>
        <Botao
          variante="fantasma"
          miudo
          icone
          rotulo="Aproximar"
          onClick={() => void mapa.zoomIn({ duration: duracao })}
        >
          <span aria-hidden="true">+</span>
        </Botao>
        <Botao
          variante="fantasma"
          miudo
          icone
          rotulo="Caber na tela"
          onClick={() => void mapa.fitView({ duration: duracao, padding: 0.2 })}
        >
          <Icone nome="lupa" />
        </Botao>
        {aoOrganizar && (
          <Botao
            variante="fantasma"
            miudo
            onClick={() => {
              aoOrganizar();
              // Depois que os blocos mudam de lugar, o mapa cabe de novo na tela.
              window.setTimeout(() => void mapa.fitView({ duration: duracao, padding: 0.2 }), 0);
            }}
          >
            <Icone nome="repetir" />
            Organizar
          </Botao>
        )}
      </div>
    </Panel>
  );
}

export function movimentoReduzido(): boolean {
  return (
    typeof globalThis.matchMedia === 'function' &&
    globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
